import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import * as yup from 'yup';

export const PALETTE_COLORS = [
  'hsl(210, 80%, 55%)', 'hsl(160, 65%, 45%)', 'hsl(260, 55%, 55%)',
  'hsl(190, 70%, 50%)', 'hsl(30, 85%, 55%)', 'hsl(340, 70%, 55%)',
  'hsl(50, 90%, 50%)', 'hsl(0, 70%, 55%)', 'hsl(120, 50%, 45%)', 'hsl(0, 0%, 60%)',
];

export type PatternSlotForm = { startTime: string; endTime: string; workingHours: number | '' };
export type PatternFormValues = {
  name: string;
  color: string;
  multiDay: boolean;
  multiDayLength: number | '';
  slots: PatternSlotForm[];
};

export type ShiftPatternRequestBody = {
  shift_pattern: {
    name: string;
    color: string;
    multi_day: boolean;
    multi_day_length: number | null;
    working_available_duration_seconds: number | null;
    sort_order: number | null;
    slots_attributes: {
      slot_order: number;
      start_time: string;
      end_time: string;
      working_available_duration_seconds: number | null;
    }[];
  };
};

const timeToSeconds = (hhmm: string): number | null => {
  const matched = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!matched) return null;
  return Number(matched[1]) * 3600 + Number(matched[2]) * 60;
};

export const slotSpanSeconds = (startTime: string, endTime: string): number | null => {
  const start = timeToSeconds(startTime);
  const end = timeToSeconds(endTime);
  if (start === null || end === null) return null;
  let diff = end - start;
  if (diff <= 0) diff += 86400;
  return diff;
};

export const blankPatternForm = (): PatternFormValues => ({
  name: '',
  color: PALETTE_COLORS[0],
  multiDay: false,
  multiDayLength: '',
  slots: [{ startTime: '09:00', endTime: '18:00', workingHours: '' }],
});

export const toFormValues = (pattern: ShiftPatternEntity): PatternFormValues => ({
  name: pattern.name,
  color: pattern.color,
  multiDay: pattern.multiDay,
  multiDayLength: pattern.multiDayLength ?? '',
  slots: pattern.slots.map((slot) => ({
    startTime: slot.startTime,
    endTime: slot.endTime,
    workingHours: slot.workingAvailableDurationSeconds === null ? '' : slot.workingAvailableDurationSeconds / 3600,
  })),
});

export const toRequestBody = (form: PatternFormValues, sortOrder: number | null): ShiftPatternRequestBody => ({
  shift_pattern: {
    name: form.name.trim(),
    color: form.color,
    multi_day: form.multiDay,
    multi_day_length: form.multiDay && form.multiDayLength !== '' ? Number(form.multiDayLength) : null,
    working_available_duration_seconds: null,
    sort_order: sortOrder,
    slots_attributes: form.slots.map((slot, index) => ({
      slot_order: index + 1,
      start_time: slot.startTime,
      end_time: slot.endTime,
      working_available_duration_seconds: slot.workingHours === '' ? null : Math.round(Number(slot.workingHours) * 3600),
    })),
  },
});

const TIME_RE = /^\d{2}:\d{2}$/;

export const schema: yup.ObjectSchema<PatternFormValues> = yup.object({
  name: yup.string().trim().required('便名を入力してください').max(255),
  color: yup.string().required('色を選択してください'),
  multiDay: yup.boolean().required(),
  multiDayLength: yup.mixed<number | ''>()
    .test('multi-day-length', '日数は2以上で入力してください', function (value) {
      if (!(this.parent as PatternFormValues).multiDay) return true;
      return typeof value === 'number' && Number.isInteger(value) && value >= 2;
    }),
  slots: yup.array().of(yup.object({
    startTime: yup.string().required('開始時刻を入力してください').matches(TIME_RE, 'HH:MM 形式で入力してください'),
    endTime: yup.string().required('終了時刻を入力してください').matches(TIME_RE, 'HH:MM 形式で入力してください'),
    workingHours: yup.mixed<number | ''>()
      .test('within-slot', '労働時間はスロットの勤務時間以内にしてください', function (value) {
        if (value === '' || value === undefined || value === null) return true;
        if (typeof value !== 'number' || value <= 0) return false;
        const parent = this.parent as PatternSlotForm;
        const span = slotSpanSeconds(parent.startTime, parent.endTime);
        if (span === null) return true;
        return value * 3600 <= span;
      }),
  })).min(1, 'スロットを1件以上追加してください').required(),
}) as yup.ObjectSchema<PatternFormValues>;
